'use client';

import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useI18n } from './i18n';
import { translateCurrent } from './i18n';

export type AppMode = 'consumer' | 'professional';

export type Vocabulary = {
  navLabel: string;
  evidence: string;
  evidencePlural: string;
  addEvidence: string;
  evidenceSaved: string;
  evidenceTimeline: string;
  noEvidence: string;
  titlePlaceholder: string;
  notePlaceholder: string;
  saveButton: string;
  activeSessionPrefix: string;
  noActiveSession: string;
  session: string;
  sessionTabLabel: string;
  newSession: string;
  sessionNameLabel: string;
  defaultSessionLabel: (date: string) => string;
  trackTab: string;
  trackingActive: string;
  trackingIdle: string;
  startTracking: string;
  stopTracking: string;
  recordPoint: string;
};

export const vocabularies: Record<AppMode, Vocabulary> = {
  consumer: {
    navLabel: 'Add memory',
    evidence: 'memory',
    evidencePlural: 'Memories',
    addEvidence: 'Save memory',
    evidenceSaved: 'Memory saved.',
    evidenceTimeline: 'Memory timeline',
    noEvidence: 'No memories yet.',
    titlePlaceholder: 'What did you see?',
    notePlaceholder: 'Add a description, note, or thought…',
    saveButton: 'Save memory',
    activeSessionPrefix: 'Tagged to',
    noActiveSession: translateCurrent('vocabulary.notOnVisit'),
    session: 'visit',
    sessionTabLabel: 'Visits',
    newSession: 'New visit',
    sessionNameLabel: 'Name this visit',
    defaultSessionLabel: (date) => translateCurrent('vocabulary.visitDate', { date }),
    trackTab: 'Track',
    trackingActive: '● Tracking',
    trackingIdle: '⊙ Paused',
    startTracking: 'Start tracking',
    stopTracking: 'Stop tracking',
    recordPoint: 'Drop a pin',
  },
  professional: {
    navLabel: 'On the go',
    evidence: 'memory',
    evidencePlural: 'Memories',
    addEvidence: 'Save a memory',
    evidenceSaved: 'Memory saved.',
    evidenceTimeline: 'Your memories',
    noEvidence: 'No memories yet.',
    titlePlaceholder: 'What do you want to remember?',
    notePlaceholder: 'Add a detail or thought…',
    saveButton: 'Save memory',
    activeSessionPrefix: 'Saved during',
    noActiveSession: translateCurrent('vocabulary.notTrackingDay'),
    session: 'day out',
    sessionTabLabel: 'Days out',
    newSession: 'New day out',
    sessionNameLabel: 'Name this day',
    defaultSessionLabel: (date) => translateCurrent('vocabulary.dayOutDate', { date }),
    trackTab: 'Record',
    trackingActive: '● REC',
    trackingIdle: '⊙ IDLE',
    startTracking: 'Start recording',
    stopTracking: 'Stop session',
    recordPoint: 'Record point',
  },
};

type VocabularyContextValue = {
  mode: AppMode;
  setMode: (mode: AppMode) => void;
  vocab: Vocabulary;
};

const storageKey = 'webapp.mode';
const defaultMode: AppMode = 'consumer';
const VocabularyContext = createContext<VocabularyContextValue | null>(null);

export function VocabularyProvider({ children }: { children: ReactNode }) {
  const { t } = useI18n();
  const [mode, setModeState] = useState<AppMode>(defaultMode);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const stored = window.localStorage.getItem(storageKey);
    if (stored === 'professional' || stored === 'consumer') setModeState(stored);
  }, []);

  const setMode = useCallback((nextMode: AppMode) => {
    setModeState(nextMode);
    window.localStorage.setItem(storageKey, nextMode);
  }, []);

  const localizedVocabularies = useMemo<Record<AppMode, Vocabulary>>(() => ({
    consumer: {
      navLabel: t('vocab.consumer.nav'), evidence: t('vocab.memory'), evidencePlural: t('vocab.memories'),
      addEvidence: t('vocab.consumer.add'), evidenceSaved: t('vocab.saved'), evidenceTimeline: t('vocab.consumer.timeline'),
      noEvidence: t('vocab.none'), titlePlaceholder: t('vocab.consumer.titlePlaceholder'), notePlaceholder: t('vocab.consumer.notePlaceholder'),
      saveButton: t('vocab.save'), activeSessionPrefix: t('vocab.consumer.activePrefix'), noActiveSession: t('vocab.consumer.noSession'),
      session: t('vocab.consumer.session'), sessionTabLabel: t('vocab.consumer.sessions'), newSession: t('vocab.consumer.newSession'),
      sessionNameLabel: t('vocab.consumer.sessionName'), defaultSessionLabel: (date) => t('vocab.consumer.defaultSession', { date }),
      trackTab: t('vocab.consumer.track'), trackingActive: t('vocab.consumer.active'), trackingIdle: t('vocab.consumer.idle'),
      startTracking: t('vocab.consumer.start'), stopTracking: t('vocab.consumer.stop'), recordPoint: t('vocab.consumer.record'),
    },
    professional: {
      navLabel: t('vocab.professional.nav'), evidence: t('vocab.memory'), evidencePlural: t('vocab.memories'),
      addEvidence: t('vocab.professional.add'), evidenceSaved: t('vocab.saved'), evidenceTimeline: t('vocab.professional.timeline'),
      noEvidence: t('vocab.none'), titlePlaceholder: t('vocab.professional.titlePlaceholder'), notePlaceholder: t('vocab.professional.notePlaceholder'),
      saveButton: t('vocab.save'), activeSessionPrefix: t('vocab.professional.activePrefix'), noActiveSession: t('vocab.professional.noSession'),
      session: t('vocab.professional.session'), sessionTabLabel: t('vocab.professional.sessions'), newSession: t('vocab.professional.newSession'),
      sessionNameLabel: t('vocab.professional.sessionName'), defaultSessionLabel: (date) => t('vocab.professional.defaultSession', { date }),
      trackTab: t('vocab.professional.track'), trackingActive: t('vocab.professional.active'), trackingIdle: t('vocab.professional.idle'),
      startTracking: t('vocab.professional.start'), stopTracking: t('vocab.professional.stop'), recordPoint: t('vocab.professional.record'),
    },
  }), [t]);

  const value = useMemo<VocabularyContextValue>(() => ({
    mode,
    setMode,
    vocab: localizedVocabularies[mode],
  }), [localizedVocabularies, mode, setMode]);

  return <VocabularyContext.Provider value={value}>{children}</VocabularyContext.Provider>;
}

export function useVocabulary() {
  return useContext(VocabularyContext) ?? {
    mode: defaultMode,
    setMode: () => {},
    vocab: vocabularies[defaultMode],
  };
}
